import React from 'react';
import mqtt, { MqttClient } from 'mqtt';
import { DateTime } from 'luxon';

import { MqttClientContext } from 'hooks/useMqttClient';

import LumeoJWT, { MqttJwtPayload } from 'types/jwt';
import { JwtParams, useJWT } from 'hooks/useJWT';
import { parseJwtWithoutVerification as parse } from 'services/jwt';
import { useAPI } from 'hooks/api/useAPI';

const host = import.meta.env.VITE_MQTT_HOST;
const port = import.meta.env.VITE_MQTT_PORT;
const protocol = import.meta.env.VITE_MQTT_PROTOCOL || 'wss';
const staticUsername = import.meta.env.VITE_MQTT_USERNAME;
const staticPassword = import.meta.env.VITE_MQTT_PASSWORD;

const url = `${protocol}://${host}:${port}`;

export function MqttClientProvider({ children }: React.PropsWithChildren) {
  const { applicationID } = useAPI();

  // Using state instead of mutation result to associate token with an application
  const [jwt, setJwt] = React.useState<LumeoJWT & JwtParams<'mqtt'>>();
  // Using state instead of top level constant because client can only initialized with mqtt.connect()
  const [client, setClient] = React.useState<MqttClient>();

  const { mutate: createMqttToken, isLoading } = useJWT<'mqtt'>(
    ['mqtt-token'],
    {
      onSuccess(data, params) {
        setJwt({ ...data, ...params });
        setClient(
          mqtt.connect(url, {
            clientId: `console_${crypto.randomUUID()}`,
            username: `application:${applicationID}`,
            password: data.token,
          })
        );
      },
    }
  );

  // For local development with static credentials
  React.useEffect(() => {
    if (!staticUsername || !staticPassword) {
      return;
    }

    const mqttClient = mqtt.connect(url, {
      clientId: `console_${crypto.randomUUID()}`,
      username: staticUsername,
      password: staticPassword,
    });

    setClient(mqttClient);

    return () => {
      mqttClient.end();
    };
  }, []);

  React.useEffect(() => {
    if (staticUsername && staticPassword) {
      return;
    }

    if (isLoading || !applicationID) {
      return;
    }

    if (
      !jwt ||
      jwt.requested_application_id_for_user !== applicationID ||
      DateTime.now().toSeconds() >= (parse<MqttJwtPayload>(jwt.token)?.exp ?? 0)
    ) {
      createMqttToken({
        type: 'mqtt',
        requested_application_id_for_user: applicationID,
      });
    }

    return () => {
      if (client) {
        client.end();
      }
    };
  }, [applicationID, client, createMqttToken, isLoading, jwt]);

  return (
    <MqttClientContext.Provider value={client}>
      {children}
    </MqttClientContext.Provider>
  );
}
